/**
 * Hook para ler eventos de auditoria da subcoleção `vacationRequests/{requestId}/events`.
 *
 * Reaproveita o padrão de `useAllocationTimelineQuery` com React Query + onSnapshot.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { VacationEvent, VacationEventType } from '../models/vacationEvent.types';

export const vacationRequestEventsQueryKeys = {
  all: ['vacationRequestEvents'] as const,
  forRequest: (requestId: string) => [...vacationRequestEventsQueryKeys.all, 'request', requestId] as const,
};

/**
 * Mapeia dados do Firestore para VacationEvent com defaults defensivos.
 */
function mapVacationEvent(doc: any): VacationEvent {
  const data = doc.data();
  return {
    id: doc.id,
    requestId: data.requestId ?? '',
    type: (data.type ?? 'direct_edit') as VacationEventType,
    actor: data.actor ?? null,
    note: data.note ?? null,
    source: (data.source ?? 'user') as 'user' | 'system',
    payload: data.payload ?? {},
    createdAt: data.createdAt
      ? data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt)
      : new Date(),
  };
}

/**
 * Hook para ler eventos de uma solicitação em tempo real.
 * Devolve { events, isLoading, error } para compatibilidade com componentes.
 */
export function useVacationRequestEventsQuery(requestId?: string) {
  const queryClient = useQueryClient();
  const queryKey = vacationRequestEventsQueryKeys.forRequest(requestId ?? '');

  const queryResult = useQuery<VacationEvent[]>({
    queryKey,
    enabled: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!requestId) return;

    const eventsKey = vacationRequestEventsQueryKeys.forRequest(requestId);
    const errorKey = [...eventsKey, 'error'];

    const q = query(
      collection(db, 'vacationRequests', requestId, 'events'),
      orderBy('createdAt', 'asc')
    );

    let hasError = false;
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const events = snap.docs.map(mapVacationEvent);
        queryClient.setQueryData(eventsKey, events);
        if (hasError) {
          queryClient.setQueryData(errorKey, null);
          hasError = false;
        }
      },
      (error) => {
        hasError = true;
        queryClient.setQueryData(errorKey, error);
        console.error('Error fetching vacation events:', error);
      }
    );

    return unsubscribe;
  }, [requestId, queryClient]);

  const errorKey = [...queryKey, 'error'] as const;
  const queryError = useQuery<any>({
    queryKey: errorKey,
    queryFn: () => null,
    enabled: false,
  });

  const error = queryResult.error || queryError.data;
  const hasLoaded = queryResult.data !== undefined;

  return {
    events: queryResult.data ?? [],
    isLoading: Boolean(requestId) && !hasLoaded && !error,
    error,
  };
}
